import Impfstelle from '@/types/Impfstelle';
import authService from '@/services/Authentication';
import router from '@/router';
import api from '@/services/Api';
import { AxiosError, AxiosResponse } from 'axios';
import { errorSwal, successSwal } from '@/helper/swals';
import User from '@/types/User';
import { ActionContext, Module } from 'vuex';
import Arbeitsplatz from '@/types/Arbeitsplatz';
import { AclModul, AclPermission, AuthenticationState, LoginData, RootState } from '@/store/types';
import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';

type Context = ActionContext<AuthenticationState, RootState>;

export const authentication: Module<AuthenticationState, RootState> = {
    namespaced: true,
    state: {
        status: {
            loggedIn: false,
            loggingIn: false,
        },
        initialized: false,
        user: null,
        token: null,
        impfstellen: [],
        impfstelle_id: 0,
        impfstelle: new Impfstelle(),
        dialog: {
            detail: false,
        },
        loaders: {
            detail: false,
            impfstelle: false,
        },
        arbeitsplaetze: [],
        permissions: {
            ivs: [],
            admin: [],
            lager: [],
            assertion: [],
            system: [],
        },
    },
    getters: {
        loggedIn: (state: AuthenticationState): boolean => {
            return state.status.loggedIn;
        },
        initialized: (state: AuthenticationState): boolean => {
            return state.initialized;
        },
        user: (state: AuthenticationState): User | null => {
            return state.user;
        },
        token: (state: AuthenticationState): string | null => {
            return state.token;
        },
        impfstelleDetail: (state: AuthenticationState): Impfstelle => {
            return state.impfstellen.find((impfstelle: Impfstelle) => {
                return impfstelle.impfstelle_id === state.impfstelle_id;
            }) ?? new Impfstelle();
        },
        getImpfstelleName: (state: AuthenticationState): string => {
            return state.impfstellen
                .find((impfstelle: Impfstelle) => impfstelle.impfstelle_id === state.impfstelle_id)?.bezeichnung ?? '';
        },
        getImpfstelleKurz: (state: AuthenticationState): string => {
            return state.impfstellen
                .find((impfstelle: Impfstelle) => impfstelle.impfstelle_id === state.impfstelle_id)?.abkuerzung ?? '';
        },
        arbeitsplatz: (state: AuthenticationState, getters: unknown, rootState: RootState): Arbeitsplatz => {
            return state.arbeitsplaetze.find((arbeitsplatz: Arbeitsplatz) => {
                return arbeitsplatz.arbeitsplatz_id === rootState.session.arbeitsplatz;
            }) ?? new Arbeitsplatz();
        },
        isAllowed: (state: AuthenticationState) => {
            return (module: AclModul | AclModul[], permission: AclPermission | AclPermission[]): boolean => {
                const permissions = Array.isArray(permission) ? permission : [permission];
                const modules = Array.isArray(module) ? module : [module];

                return modules.some((module) => {
                    return (state.permissions[module] ?? []).some((allowed) => {
                        return permissions.some((target) => target === allowed);
                    });
                });
            };
        },
        getSystemEinstellung: <T>(state: AuthenticationState) => {
            return (parameter: SystemEinstellungKey): SystemEinstellung<T> => {
                return new SystemEinstellung<T>(
                    state.user?.system_einstellungen
                        ? state.user?.system_einstellungen[parameter] as unknown as T
                        : null as unknown as T
                );
            };
        },
    },
    actions: {
        login({ commit, dispatch, rootState }: Context, payload: LoginData): Promise<void> {
            commit('loginRequest');

            return authService
                .login(payload)
                .then((response: AxiosResponse) => {
                    commit('initialized', true);

                    if (response && response.data && response.data.body) {
                        dispatch('loadPermissions');
                        const user = new User(response.data.body);

                        const reloadImpfstelle = user.impfstelle_id !== rootState.session.impfstelle;

                        if (rootState.session.impfstelle) {
                            user.impfstelle_id = rootState.session.impfstelle;
                        }

                        commit('loginSuccess', user);
                        dispatch('session/setSelectedArzt', null, { root: true });

                        if (reloadImpfstelle) {
                            dispatch('loadImpfstelle');
                        }

                        const location = user.hasRoles(['lager']) && !user.hasRoles(['admin', 'admin', 'system'])
                            ? '/lager' : '/';

                        if (router.currentRoute.path !== location) {
                            router.push(location);
                        }

                        if (user.elga_error) {
                            setTimeout(() => {
                                errorSwal('e-Card Fehler', user.elga_error?.toString());
                            }, 3000);
                        }
                    } else {
                        commit('resetLogin');
                        if (router.currentRoute.meta?.requiresAuth !== undefined &&
                            router.currentRoute.meta?.requiresAuth) {
                            router.push('/login');
                        }
                        return Promise.reject(new Error());
                    }
                })
                .catch((error: AxiosError) => {
                    commit('resetLogin');
                    console.error(error);
                    errorSwal('Anmeldung fehlgeschlagen', 'Benutzername oder Passwort.');
                });
        },
        start({ commit, dispatch, rootState }: Context): Promise<void> {
            commit('loginRequest');

            return authService
                .start()
                .then((response) => {
                    commit('initialized', true);

                    if (response && response.data && response.data.body) {
                        dispatch('loadPermissions');

                        const user = new User(response.data.body);

                        //console.log(user.roles);
                        //user.roles = ['lager', 'admin', 'anwender'];

                        const reloadImpfstelle = user.impfstelle_id !== rootState.session.impfstelle;

                        if (rootState.session.impfstelle) {
                            user.impfstelle_id = rootState.session.impfstelle;
                        }

                        commit('loginSuccess', user);
                        dispatch('session/setSelectedArzt', null, { root: true });

                        if (reloadImpfstelle) {
                            dispatch('loadImpfstelle');
                        }

                        const location = user.hasRoles(['lager']) && !user.hasRoles(['admin', 'admin', 'system'])
                            ? '/lager'
                            : '/';

                        if (router.currentRoute.path !== location) {
                            router.push(location);
                        }
                    } else {
                        commit('resetLogin');
                        if (router.currentRoute.meta?.requiresAuth !== undefined &&
                            router.currentRoute.meta.requiresAuth) {
                            router.push('/login');
                        }
                        return Promise.reject(new Error());
                    }
                }).catch(() => {
                    commit('initialized', true);
                    commit('resetLogin');
                    if (router.currentRoute.meta?.requiresAuth !== undefined && router.currentRoute.meta.requiresAuth) {
                        router.push('/login');
                    }
                    return Promise.reject(new Error());
                });
        },
        clear({ commit }: Context): void {
            commit('resetLogin');
        },
        logout({ commit, dispatch }: Context): void {
            commit('initialized', false);

            authService
                .logout()
                .then(() => {
                    dispatch('clear').then(() => commit('logout'));

                    if (router.currentRoute.meta?.requiresAuth !== undefined && router.currentRoute.meta.requiresAuth) {
                        router.go(0);
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    commit('initialized', true);
                });
        },
        unauthorized({ commit, state }: Context): void {
            if (state.status.loggedIn) {
                commit('sessionExpired');
            } else {
                if (state.initialized && router.currentRoute.meta?.requiresAuth === true) {
                    const path = router.currentRoute.fullPath;
                    router.push({ path: '/login', query: { redirect: path } });
                } else {
                    commit('resetLogin', null);
                }
            }
        },
        updateImpfstelle({ commit, dispatch }: Context, payload: number): void {
            commit('updateImpfstelle', payload);
            dispatch('session/setSelectedImpfstelle', payload, { root: true });
            dispatch('loadImpfstelle');

            const dataUpdate: { [key: string]: string } = {
                '/kassa': 'kassafuehrung/loadKassaabschluesse',
                '/personen': 'personen/loadPersonen',
                '/arbeitsplaetze': 'arbeitsplaetze/loadArbeitsplaetze',
            };

            const update = dataUpdate[router.currentRoute.path];
            if (update) {
                dispatch(update, null, { root: true });
            }
        },
        updateData({ dispatch }: Context): void {
            dispatch('personen/loadPersonen', null, { root: true });
            dispatch('impfstoff/loadImpfstoffchargen', null, { root: true });
            dispatch('lagerfuehrung/loadImpfstoffe', null, { root: true });
            dispatch('kassafuehrung/loadKassaabschluesse', null, { root: true });
        },
        updateImpfstelleDetail({ commit, dispatch }: Context, payload: Impfstelle): void {
            commit('setLoadersDetail', true);

            api.post('verwaltung/impfstellen/speichern', payload)
                .then(() => {
                    dispatch('loadImpfstellen');
                    commit('setDialogImpfstelleDetail', false);
                    successSwal('Impfstelle', 'gespeichert');
                })
                .catch(console.error)
                .finally(() => commit('setLoadersDetail', false));
        },
        loadImpfstelle({ commit, state }: Context): void {
            commit('setLoadersImpfstelle', true);

            const modul = state.user?.hasRoles(['lager']) && !state.user?.hasRoles(['anwender', 'admin', 'system'])
                ? 'lager'
                : 'verwaltung';

            api.post(`${modul}/impfstellen/index`, { impfstelle_id: state.impfstelle_id })
                .then((response: AxiosResponse) => commit('setImpfstelleDetails', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoadersImpfstelle', false));
        },
        loadImpfstellen({ commit }: Context): void {
            api.post('verwaltung/impfstellen/loadall')
                .then((response: AxiosResponse) => commit('setImpfstellen', response.data.body))
                .catch(console.error);
        },
        loadPermissions({ commit }: Context): void {
            api.post('authentication/permissions')
                .then((response: AxiosResponse) => commit('setPermissions', response.data.body))
                .catch(console.error);
        },
        updateUser({ commit }: Context, user?: Partial<User>): void {
            commit('updateUser', user);
        },
    },
    mutations: {
        initialized(state: AuthenticationState, value: boolean): void {
            state.initialized = value;
        },
        loginRequest(state: AuthenticationState): void {
            state.status = { loggingIn: true, loggedIn: false };
            state.user = null;
        },
        resetLogin(state: AuthenticationState): void {
            state.status = { loggedIn: false, loggingIn: false };
            state.user = null;
            state.token = null;
        },
        loginSuccess(state: AuthenticationState, user: User): void {
            state.status = { loggedIn: true, loggingIn: false };
            state.user = user;
            state.impfstelle_id = user.impfstelle_id as number;
            state.impfstellen = user.impfstellen as Impfstelle[];
            state.arbeitsplaetze = user.arbeitsplaetze as Arbeitsplatz[];
        },
        updateUser(state: AuthenticationState, user?: Partial<User>): void {
            if (state.user && user) {
                Object.assign(state.user, user);
            }
        },
        logout(state: AuthenticationState): void {
            state.status = { loggedIn: false, loggingIn: false };
            state.user = null;
            state.token = null;
        },
        updateImpfstelle(state: AuthenticationState, id: number): void {
            state.impfstelle_id = id;
            state.arbeitsplaetze = [];
        },
        setDialogImpfstelleDetail(state: AuthenticationState, visible: boolean): void {
            state.dialog.detail = visible;
        },
        setImpfstelleDetails(state: AuthenticationState, payload: Impfstelle): void {
            state.impfstelle = payload;
            state.impfstelle_id = payload.impfstelle_id as number;
            state.arbeitsplaetze = payload.arbeitsplaetze as Arbeitsplatz[];

            const { impfstelle_id } = state.impfstelle;
            const impfstelle = state.impfstellen.find((impfstelle: Impfstelle) => {
                return impfstelle.impfstelle_id === impfstelle_id;
            });

            if (impfstelle) {
                Object.assign(impfstelle, state.impfstelle);
            }
        },
        setImpfstellen(state: AuthenticationState, payload: Impfstelle[]): void {
            state.impfstellen = payload;
        },
        setLoadersDetail(state: AuthenticationState, visible: boolean): void {
            state.loaders.detail = visible;
        },
        setLoadersImpfstelle(state: AuthenticationState, visible: boolean): void {
            state.loaders.impfstelle = visible;
        },
        setPermissions(state: AuthenticationState, permissions: { [key in AclModul]: AclPermission[] }): void {
            state.permissions = permissions;
        },
    },
};
